import { createApi } from "@reduxjs/toolkit/query/react";
import { baseQueryWithReauth } from "../api.config";

export const affiliateApi = createApi({
  reducerPath: "affiliateApi",
  baseQuery: baseQueryWithReauth,
  tagTypes: ['Affiliate', 'Payout'],
  endpoints: (build) => ({
    // Calculate eligible payout for an affiliate
    calculateEligiblePayout: build.mutation({
      query: ({ affiliateId, referrals }) => ({
        url: "affiliates/calculate-eligible-payout",
        method: "POST",
        body: { affiliateId, referrals },
      }),
    }),

    // Create Stripe Connect account for affiliate
    createConnectAccount: build.mutation({
      query: ({ affiliateId, email, country = 'US' }) => ({
        url: `affiliates/${affiliateId}/create-connect-account`,
        method: "POST",
        body: { email, country },
      }),
      invalidatesTags: ['Affiliate'],
    }),

    // Process affiliate payout
    processPayout: build.mutation({
      query: ({ affiliateId, amount, commissionIds, eligibleCommissions, stripeConnectAccountId }) => ({
        url: "affiliates/process-payout",
        method: "POST",
        body: { affiliateId, amount, commissionIds, eligibleCommissions, stripeConnectAccountId },
      }),
      invalidatesTags: ['Payout', 'Affiliate'],
    }),

    // Get all affiliates
    getAllAffiliates: build.query({
      query: () => "affiliates",
      providesTags: ['Affiliate'],
    }),

    // Approve affiliate (admin only)
    approveAffiliate: build.mutation({
      query: ({ affiliateId, ...data }) => ({
        url: `affiliates/${affiliateId}/approve`,
        method: "POST",
        body: data,
      }),
      invalidatesTags: ['Affiliate'],
    }),

    // Deny affiliate (admin only)
    denyAffiliate: build.mutation({
      query: ({ affiliateId, ...data }) => ({
        url: `affiliates/${affiliateId}/deny`,
        method: "POST",
        body: data,
      }),
      invalidatesTags: ['Affiliate'],
    }),

    // Delete affiliate (admin only)
    deleteAffiliate: build.mutation({
      query: (affiliateId) => ({
        url: `affiliates/${affiliateId}`,
        method: "DELETE",
      }),
      invalidatesTags: ['Affiliate'],
    }),

    // Send questionnaire to applicant
    sendQuestionnaire: build.mutation({
      query: (affiliateId) => ({
        url: `affiliates/${affiliateId}/send-questionnaire`,
        method: "POST",
      }),
      invalidatesTags: ['Affiliate'],
    }),

    // Send affiliate agreement for e-signature
    sendAgreement: build.mutation({
      query: (affiliateId) => ({
        url: `affiliates/${affiliateId}/send-agreement`,
        method: "POST",
      }),
      invalidatesTags: ['Affiliate'],
    }),

    // Run AI assessment on affiliate application
    aiAssessAffiliate: build.mutation({
      query: (affiliateId) => ({
        url: `affiliates/${affiliateId}/ai-assess`,
        method: "POST",
      }),
      invalidatesTags: ['Affiliate'],
    }),
  }),
});

export const {
  useCalculateEligiblePayoutMutation,
  useCreateConnectAccountMutation,
  useProcessPayoutMutation,
  useGetAllAffiliatesQuery,
  useApproveAffiliateMutation,
  useDenyAffiliateMutation,
  useDeleteAffiliateMutation,
  useSendQuestionnaireMutation,
  useSendAgreementMutation,
  useAiAssessAffiliateMutation,
} = affiliateApi;
